import { useEffect, useState } from 'react';
import Modal from '@/components/Modal';
import { Button } from '@/components/Button';
import { apiGetRequest, apiPostRequest } from '@/utils/apiClient';
import { detectDeviceAndBrowser } from '@/utils/deviceAndBrowser';
import { getAppType } from '@/utils/loginInfo';
import { useTranslation } from 'react-i18next';

const FEEDBACK_CODE = 'BASICPLAN_PURCHASE';

const DEFAULT_OPTION_KEYS = [
    'subscriptionFeedback.options.adFree',
    'subscriptionFeedback.options.longerAnalysisPeriod',
    'subscriptionFeedback.options.moreScheduleSlots',
    'subscriptionFeedback.options.periodComparison',
    'subscriptionFeedback.options.moreActivityKeywords',
    'subscriptionFeedback.options.customLayout',
    'subscriptionFeedback.options.other',
];

type FeedbackTemplateItems = {
    options?: boolean;
    comment?: boolean;
};

type FeedbackTemplateResponse = {
    feedbackCode: string;
    version: number;
    items?: FeedbackTemplateItems;
};

type SubscriptionFeedbackModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

const SubscriptionFeedbackModal = ({ isOpen, onClose }: SubscriptionFeedbackModalProps) => {
    const { t, i18n } = useTranslation(['checkout', 'onboarding']);

    const [template, setTemplate] = useState<FeedbackTemplateResponse | null>(null);
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const resetState = () => {
        setTemplate(null);
        setSelectedOptions([]);
        setIsLoading(false);
        setIsSubmitting(false);
        setValidationError(null);
        setSubmitError(null);
    };

    useEffect(() => {
        if (!isOpen) {
            resetState();
            return;
        }

        const loadTemplate = async () => {
            setIsLoading(true);
            try {
                const localeParam = encodeURIComponent(i18n.language ?? '');
                const response = await apiGetRequest(
                    `/api/feedback/templates/${FEEDBACK_CODE}?locale=${localeParam}`,
                );
                if (!response.ok) {
                    throw new Error('failed to load template');
                }
                const data: FeedbackTemplateResponse = await response.json();
                setTemplate(data);
            } catch (error) {
                onClose();
            } finally {
                setIsLoading(false);
            }
        };

        void loadTemplate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, i18n.language]);

    const optionsEnabled = template?.items?.options ?? true;

    const toggleOption = (optionKey: string) => {
        if (!optionsEnabled) {
            return;
        }
        setSelectedOptions((prev) => {
            if (prev.includes(optionKey)) {
                return prev.filter((key) => key !== optionKey);
            }
            return [...prev, optionKey];
        });
    };

    const handleSubmit = async () => {
        const options = optionsEnabled ? selectedOptions : [];
        if (optionsEnabled && options.length === 0) {
            setValidationError(t('feedback.validation.optionRequired', { ns: 'onboarding' }));
            return;
        }
        setValidationError(null);
        setSubmitError(null);
        setIsSubmitting(true);

        try {
            if (template) {
                const translatedOptions = options.map((key) => t(key));
                const response = await apiPostRequest('/api/feedback/submit', {
                    feedbackCode: template.feedbackCode ?? FEEDBACK_CODE,
                    templateVersion: template.version,
                    selectedOptions: translatedOptions,
                    comment: null,
                    deviceBrowser: detectDeviceAndBrowser(),
                    appType: getAppType(),
                    locale: i18n.language,
                });
                if (!response.ok) {
                    throw new Error('feedback submit failed');
                }
            }
            onClose();
        } catch (error) {
            setSubmitError(t('feedback.submitError', { ns: 'onboarding' }));
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => undefined}
            contentClassName="w-full max-w-2xl p-8"
            closeButtonClassName="hidden"
            disableCloseOnEsc={true}
            disableCloseOnOverlay={true}
        >
            <div className="space-y-6">
                <div className="text-center space-y-2">
                    <h2 className="text-xl font-semibold">{t('subscriptionFeedback.title')}</h2>
                </div>

                {isLoading && (
                    <p className="text-sm text-gray-500 text-center">
                        {t('feedback.loading', { ns: 'onboarding' })}
                    </p>
                )}

                {!isLoading && optionsEnabled && (
                    <div className="space-y-4">
                        <p className="text-sm text-gray-700 font-medium text-center">
                            {t('subscriptionFeedback.optionLabel')}
                        </p>
                        <div className="space-y-2">
                            {DEFAULT_OPTION_KEYS.map((optionKey) => {
                                const label = t(optionKey);
                                return (
                                    <label
                                        key={optionKey}
                                        className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:border-blue-400 transition cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            className="mt-1 h-4 w-4"
                                            checked={selectedOptions.includes(optionKey)}
                                            onChange={() => toggleOption(optionKey)}
                                            disabled={isSubmitting}
                                        />
                                        <span className="text-sm text-gray-700">{label}</span>
                                    </label>
                                );
                            })}
                        </div>
                        {validationError && (
                            <p className="text-sm text-red-600 text-center">{validationError}</p>
                        )}
                    </div>
                )}

                {submitError && <p className="text-sm text-red-600 text-center">{submitError}</p>}

                <div className="flex justify-center gap-4 pt-2">
                    <Button
                        variant="original"
                        onClick={handleSubmit}
                        isLoading={isSubmitting}
                        loadingLabel={t('subscriptionFeedback.sending')}
                        className="w-auto px-6"
                    >
                        {t('subscriptionFeedback.sendButton')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default SubscriptionFeedbackModal;
